import { color, FormatUtils } from '@my/util'

const monthAbbrevArray = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const valueToMonthData = <T extends any>(valueFn: (idx: number) => T, array: number[]) => {
  const thisMonth = (new Date()).getMonth();
  return array.map((offset, idx) => ({ value: valueFn(idx), month: monthAbbrevArray[(thisMonth+offset) % 11] }));
};

//Cash Flow Analysis
// Format the month string to a more readable format
export const formatMonth = (dateStr) =>
  new Date(dateStr + '-01').toLocaleString('default', { month: 'short' })

// Build data for the financial review table from the API response
export const buildDataFromAPI = (apiResponse) => {
  const result = [
    { Assumptions: 'Accumulated Spending' },
    { Assumptions: 'Forecasted Spending' },
    { Assumptions: 'Monthly Income' },
  ]

  apiResponse?.forEach((apiItem) => {
    const monthName = apiItem.month // Use label as the month name
    result[0][monthName] = FormatUtils.currency.cashFormat(apiItem.totalSpendingToDate, {decimalPlaces: 0});
    result[1][monthName] = FormatUtils.currency.cashFormat(apiItem.spendingThisMonth, {decimalPlaces: 0});
    result[2][monthName] = FormatUtils.currency.cashFormat(apiItem.monthlyIncome, {decimalPlaces: 0});
  })

  return result
}

// Build available cash data to be displayed in the table
export const buildAvailableCashData = (apiResponse) => {
  const header = {
    text: 'Forecasted Balance',
    bold: true,
    borderRightWidthFooter: 1,
    color: color.tzaGreen,
  }

  const cashData = apiResponse?.map((item) => ({
    text: FormatUtils.currency.cashFormat(item.availableCash, {decimalPlaces: 0}), // Use item.data.availableCash
    color: 'rgb(30, 31, 36)', //color.lightGrey,
    bold: true,
    borderRightWidthFooter: 1,
  }))

  return [header, ...cashData]
}

// Transform API response into chart data (bar and line charts)
export const transformApiResponse = (apiResponse) => {
  const barData = [
    {
      name: 'Income',
      data: apiResponse.map((item) => ({
        month: formatMonth(item.date), // Using 'date' from cashFlowAnalysisUI
        value: parseFloat(item.data.totalIncome), // Using totalIncome for bar chart
      })),
    },
    {
      name: 'Expenses',
      data: apiResponse.map((item) => ({
        month: formatMonth(item.date),
        value: parseFloat(item.data.totalSpending), // Using totalSpending for bar chart
      })),
    },
  ]

  // The line data will be based on 'difference' from the UI data
  const lineData = apiResponse.map((item) => ({
    name: 'Trend',
    month: formatMonth(item.date),
    value: parseFloat(item.data.difference), // Using the difference for line chart
  }))

  return { barData, lineData }
}

//Scenerio Planning
// Define constants for headings and table data
export const DEFAULT_TABLE_HEADINGS = [
  { key: 'category', text: 'Source' },
  { key: 'amount', text: 'Amount' },
  { key: 'frequency', text: 'Frequency' },
  { key: 'date', text: 'Date' },
]

export const DEFAULT_TABLE_DATA = [
  { category: 'Salary', amount: '$5000', frequency: 'Monthly', date: '07/01/2024' },
  { category: 'Freelance Work', amount: '$5000', frequency: 'Monthly', date: '07/01/2024' },
  { category: 'Interest Income', amount: '$5000', frequency: 'Monthly', date: '07/01/2024' },
]

export const SCENARIO_CHART_DATA = {
  data1: [
    { x: 10, y: 30 },
    { x: 11, y: 20 },
    { x: 12, y: 15 },
    { x: 13, y: 10 },
    { x: 14, y: 25 },
    { x: 15, y: 25 },
    { x: 16, y: 25 },
  ],
  data2: [
    { x: 10, y: 20 },
    { x: 11, y: 10 },
    { x: 12, y: 10 },
    { x: 13, y: 8 },
    { x: 14, y: 5 },
    { x: 15, y: 5 },
    { x: 16, y: 5 },
  ],
  data3: [
    { x: 10, y: 10 },
    { x: 11, y: 5 },
    { x: 12, y: 5 },
    { x: 13, y: 2 },
    { x: 14, y: 10 },
    { x: 15, y: 10 },
    { x: 16, y: 10 },
  ],
  data4: [
    { x: '8%', y: 8, label: '8%' },
    { x: '18%', y: 18, label: '18%' },
    { x: '24%', y: 24, label: '24%' },
    { x: '50%', y: 50, label: '50%' },
  ],
  lineBarData: [
    { month: 'Jan', bar1: 200, bar2: 200, line: 300 },
    { month: 'Feb', bar1: 100, bar2: 100, line: 400 },
    { month: 'Mar', bar1: 150, bar2: 150, line: 300 },
    { month: 'Apr', bar1: 250, bar2: 250, line: 200 },
    { month: 'May', bar1: 350, bar2: 350, line: 400 },
    { month: 'Jun', bar1: 400, bar2: 400, line: 500 },
    { month: 'Jul', bar1: 300, bar2: 300, line: 600 },
    { month: 'Aug', bar1: 350, bar2: 350, line: 500 },
    { month: 'Sep', bar1: 150, bar2: 150, line: 400 },
    { month: 'Oct', bar1: 200, bar2: 200, line: 500 },
    { month: 'Nov', bar1: 300, bar2: 300, line: 600 },
    { month: 'Dec', bar1: 450, bar2: 450, line: 700 },
  ],
  // StackedBar and Donut chart data
  worthData: [
    { x: '8%', y: 8, label: '8%' },
    { x: '18%', y: 18, label: '18%' },
    { x: '24%', y: 24, label: '24%' },
    { x: '50%', y: 50, label: '50%' },
  ],

  dataTeal: [
    { x: 'Jan', y: 400 },
    { x: 'Feb', y: 100 },
    { x: 'Mar', y: 300 },
    { x: 'Apr', y: 200 },
    { x: 'May', y: 400 },
    { x: 'Jun', y: 1000 },
    { x: 'Jul', y: 800 },
    { x: 'Aug', y: 500 },
    { x: 'Sep', y: 200 },
    { x: 'Oct', y: 300 },
    { x: 'Nov', y: 800 },
    { x: 'Dec', y: 900 },
  ],
  dataRed: [
    { x: 'Jan', y: 0 },
    { x: 'Feb', y: 0 },
    { x: 'Mar', y: 100 },
    { x: 'Apr', y: 0 },
    { x: 'May', y: 100 },
    { x: 'Jun', y: 200 },
    { x: 'Jul', y: 200 },
    { x: 'Aug', y: 100 },
    { x: 'Sep', y: 0 },
    { x: 'Oct', y: 100 },
    { x: 'Nov', y: 100 },
    { x: 'Dec', y: 100 },
  ],
}

export const scenarioDataTable = [
  { label: 'Event Description', detail: 'Purchase a new home' },
  { label: 'Date', detail: '10/01/2024' },
  { label: 'Financial Impact', detail: 'Increase Expense' },
  {
    label: 'Impact Amount',
    detail: '$1,500 monthly mortgage, $20,000 down payment, $300 monthly property taxes',
  },
  { label: 'Frequency', detail: 'Monthly for mortgage and taxes, One-time for down payment' },
]

export const scenarioItems = [
  { label: 'Buying a home', value: 'Buying a home' },
  { label: 'Changing jobs', value: 'Changing jobs' },
  { label: 'Major medical expense', value: 'Major medical expense' },
  { label: 'Unexpected home repair', value: 'Unexpected home repair' },
  { label: 'Starting a business', value: 'Starting a business' },
]

export const financialImpact = [
  { label: 'Increase Income', value: 'Increase Income' },
  { label: 'Decrease Income', value: 'Decrease Income' },
  { label: 'Increase Expense', value: 'Increase Expense' },
]

export const frequencyData = [
  { label: 'One-time', value: 'One-time' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Yearly', value: 'Yearly' },
]
